import React, { useEffect, useState } from 'react';
import { Bell, BellRing, BellOff, Clock, CheckCheck, Trash2, Filter, Smartphone, Globe, Sparkles, Heart, BookOpen, MessageCircle, Calendar, Pill, Award, ChevronRight, RefreshCw, X } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import BottomNav from '../components/BottomNav';
import { useNotifications } from '../context/NotificationContext';
import { toast } from '../hooks/use-toast';
import { Capacitor } from '@capacitor/core';
import { pushNotificationService } from '../services/pushNotifications';
import { requestNotificationPermission, getWebPushToken } from '../services/firebaseWeb';
import api from '../services/api';
import { useNavigate } from 'react-router-dom';

const NOTIF_ICONS = {
  cycle_alert: Heart, period_coming: Calendar, ovulation: Sparkles,
  fertile_window: Heart, contraception: Pill, daily_pill: Pill,
  quiz_result: BookOpen, module_complete: Award, checkin: Award,
  community: MessageCircle, custom_alert: Bell, badge: Award,
  level_up: Sparkles, streak: Sparkles, test: Bell, default: Bell
};

const NOTIF_COLORS = {
  cycle_alert: 'from-red-500/20 to-pink-500/20 border-red-400/30',
  period_coming: 'from-red-500/20 to-rose-500/20 border-red-400/30',
  ovulation: 'from-purple-500/20 to-pink-500/20 border-purple-400/30',
  fertile_window: 'from-pink-500/20 to-rose-500/20 border-pink-400/30',
  contraception: 'from-blue-500/20 to-cyan-500/20 border-blue-400/30',
  daily_pill: 'from-blue-500/20 to-indigo-500/20 border-blue-400/30',
  quiz_result: 'from-yellow-500/20 to-orange-500/20 border-yellow-400/30',
  module_complete: 'from-green-500/20 to-emerald-500/20 border-green-400/30',
  checkin: 'from-amber-500/20 to-yellow-500/20 border-amber-400/30',
  community: 'from-violet-500/20 to-purple-500/20 border-violet-400/30',
  badge: 'from-yellow-500/20 to-amber-500/20 border-yellow-400/30',
  level_up: 'from-cyan-500/20 to-blue-500/20 border-cyan-400/30',
  streak: 'from-orange-500/20 to-red-500/20 border-orange-400/30',
  default: 'from-pink-500/20 to-purple-500/20 border-pink-400/30'
};

const NOTIF_ROUTES = {
  cycle_alert: '/calendar', period_coming: '/calendar', ovulation: '/calendar',
  fertile_window: '/calendar', contraception: '/health', daily_pill: '/health',
  quiz_result: '/modules', module_complete: '/modules', checkin: '/dashboard',
  community: '/community', badge: '/profile', level_up: '/profile',
  streak: '/dashboard'
};

const NotificationsPage = () => {
  const { notifications, unreadCount, loading, fetchNotifications, markAsRead, markAllRead, deleteNotification, clearAll } = useNotifications();
  const [filter, setFilter] = useState('all');
  const [pushEnabled, setPushEnabled] = useState(false);
  const [devicesCount, setDevicesCount] = useState(0);
  const [activatingPush, setActivatingPush] = useState(false);
  const navigate = useNavigate();
  const isNative = Capacitor.isNativePlatform();

  useEffect(() => {
    fetchNotifications();
    checkPushStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkPushStatus = async () => {
    try {
      const res = await api.get('/notifications/push-status');
      setPushEnabled(res.data.pushEnabled);
      setDevicesCount(res.data.devicesCount || 0);
    } catch (e) {}
  };

  const enablePush = async () => {
    setActivatingPush(true);
    try {
      if (isNative) {
        await pushNotificationService.initialize();
      } else {
        const perm = await requestNotificationPermission();
        if (perm.granted) {
          const token = await getWebPushToken();
          if (token) {
            await api.post('/notifications/register-device', { token, platform: 'web' });
          }
        } else {
          toast({ title: 'Permission refusee', description: 'Activez les notifications dans les parametres du navigateur', variant: 'destructive' });
          return;
        }
      }
      setTimeout(async () => {
        await checkPushStatus();
        toast({ title: 'Notifications activees !' });
      }, 1500);
    } catch (e) {
      toast({ title: 'Erreur', variant: 'destructive' });
    } finally {
      setActivatingPush(false);
    }
  };

  const handleNotifClick = (notif) => {
    if (!notif.read) markAsRead(notif.id);
    const route = NOTIF_ROUTES[notif.type];
    if (route) navigate(route);
  };

  const formatTime = (t) => {
    const d = new Date(t);
    const now = new Date();
    const diff = now - d;
    const mins = Math.floor(diff / 60000);
    const hrs = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    if (mins < 1) return "A l'instant";
    if (mins < 60) return `Il y a ${mins}min`;
    if (hrs < 24) return `Il y a ${hrs}h`;
    if (days === 1) return 'Hier';
    if (days < 7) return `Il y a ${days}j`;
    return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };

  const filtered = notifications.filter(n => {
    if (filter === 'unread') return !n.read;
    if (filter === 'health') return ['cycle_alert', 'period_coming', 'ovulation', 'fertile_window', 'contraception', 'daily_pill'].includes(n.type);
    if (filter === 'education') return ['quiz_result', 'module_complete'].includes(n.type);
    if (filter === 'social') return ['community', 'badge', 'level_up', 'streak', 'checkin'].includes(n.type);
    return true;
  });

  const grouped = {};
  filtered.forEach(n => {
    const d = new Date(n.time);
    const today = new Date();
    const yesterday = new Date(today); yesterday.setDate(today.getDate() - 1);
    let key;
    if (d.toDateString() === today.toDateString()) key = "Aujourd'hui";
    else if (d.toDateString() === yesterday.toDateString()) key = "Hier";
    else key = d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(n);
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-pink-800 to-purple-900 pb-24" data-testid="notifications-page">
      {/* Header */}
      <div className="sticky top-0 bg-purple-900/90 backdrop-blur-xl border-b border-white/10 px-4 py-3 z-40">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Bell className="w-6 h-6 text-pink-300" />
            <h1 className="text-xl font-bold text-white">Notifications</h1>
            {unreadCount > 0 && (
              <span className="px-2 py-0.5 bg-red-500 text-white text-xs font-bold rounded-full" data-testid="unread-badge">{unreadCount}</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {unreadCount > 0 && (
              <Button size="sm" variant="ghost" onClick={markAllRead} className="text-pink-200 hover:text-white hover:bg-white/10 text-xs h-8" data-testid="mark-all-read">
                <CheckCheck className="w-4 h-4 mr-1" /> Tout lire
              </Button>
            )}
            <Button size="sm" variant="ghost" onClick={fetchNotifications} className="text-pink-200 hover:text-white hover:bg-white/10 h-8 w-8 p-0" data-testid="refresh-notifs">
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex gap-2 mt-3 overflow-x-auto pb-1 scrollbar-hide">
          {[
            { key: 'all', label: 'Tout' },
            { key: 'unread', label: `Non lues (${unreadCount})` },
            { key: 'health', label: 'Sante' },
            { key: 'education', label: 'Education' },
            { key: 'social', label: 'Social' }
          ].map(f => (
            <button key={f.key} onClick={() => setFilter(f.key)}
              className={`px-3 py-1 rounded-full text-xs whitespace-nowrap transition-all ${
                filter === f.key ? 'bg-pink-500 text-white' : 'bg-white/10 text-pink-200 hover:bg-white/20'
              }`} data-testid={`filter-${f.key}`}>
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-4 space-y-4">
        {/* Push Status */}
        <Card className={`p-4 ${pushEnabled ? 'bg-green-500/10 border-green-400/20' : 'bg-orange-500/10 border-orange-400/20'}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${pushEnabled ? 'bg-green-500/20' : 'bg-orange-500/20'}`}>
                {pushEnabled ? <BellRing className="w-5 h-5 text-green-300" /> : <BellOff className="w-5 h-5 text-orange-300" />}
              </div>
              <div>
                <p className="text-white font-semibold text-sm">Notifications Push</p>
                <p className="text-pink-200 text-xs">
                  {pushEnabled ? (
                    <><Smartphone className="w-3 h-3 inline" /> {devicesCount} appareil(s) | <Globe className="w-3 h-3 inline" /> {isNative ? 'Mobile' : 'Web'}</>
                  ) : 'Activez pour ne rien manquer'}
                </p>
              </div>
            </div>
            {!pushEnabled && (
              <Button size="sm" onClick={enablePush} disabled={activatingPush}
                className="bg-pink-500 hover:bg-pink-600 text-white text-xs" data-testid="enable-push-btn">
                {activatingPush ? '...' : 'Activer'}
              </Button>
            )}
          </div>
        </Card>

        {/* Notification List */}
        {loading ? (
          <div className="text-center text-pink-200 py-12">Chargement...</div>
        ) : filtered.length === 0 ? (
          <Card className="p-12 bg-white/5 border-white/10 text-center">
            <Bell className="w-14 h-14 text-pink-300/30 mx-auto mb-4" />
            <h3 className="text-white font-semibold mb-2">
              {filter === 'unread' ? 'Tout est lu !' : 'Aucune notification'}
            </h3>
            <p className="text-pink-200 text-sm">
              {filter === 'unread' ? 'Vous etes a jour.' : 'Les notifications apparaitront ici.'}
            </p>
          </Card>
        ) : (
          Object.entries(grouped).map(([date, notifs]) => (
            <div key={date}>
              <p className="text-pink-300/70 text-xs font-semibold uppercase tracking-wider mb-2 pl-1">{date}</p>
              <div className="space-y-2">
                {notifs.map(notif => {
                  const Icon = NOTIF_ICONS[notif.type] || NOTIF_ICONS.default;
                  const colorClass = NOTIF_COLORS[notif.type] || NOTIF_COLORS.default;
                  const hasRoute = !!NOTIF_ROUTES[notif.type];

                  return (
                    <Card key={notif.id}
                      onClick={() => handleNotifClick(notif)}
                      className={`p-4 bg-gradient-to-br ${colorClass} cursor-pointer hover:scale-[1.01] transition-all ${!notif.read ? 'ring-1 ring-pink-400/40' : 'opacity-75'}`}
                      data-testid={`notif-${notif.id}`}>
                      <div className="flex items-start gap-3">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${!notif.read ? 'bg-white/20' : 'bg-white/10'}`}>
                          <Icon className="w-5 h-5 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <h4 className={`font-semibold text-sm ${!notif.read ? 'text-white' : 'text-white/80'}`}>{notif.title}</h4>
                            <div className="flex items-center gap-1 flex-shrink-0">
                              {!notif.read && <div className="w-2 h-2 rounded-full bg-pink-400" />}
                              <button onClick={(e) => { e.stopPropagation(); deleteNotification(notif.id); }}
                                className="p-1 hover:bg-white/10 rounded text-pink-300/50 hover:text-red-400 transition-colors" data-testid={`delete-notif-${notif.id}`}>
                                <X className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>
                          <p className="text-pink-100/80 text-xs mt-0.5 line-clamp-2">{notif.message}</p>
                          <div className="flex items-center justify-between mt-2">
                            <span className="text-pink-300/60 text-[11px] flex items-center gap-1">
                              <Clock className="w-3 h-3" /> {formatTime(notif.time)}
                            </span>
                            {hasRoute && (
                              <span className="text-pink-300/60 text-[11px] flex items-center gap-0.5">
                                Voir <ChevronRight className="w-3 h-3" />
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
            </div>
          ))
        )}

        {/* Clear All */}
        {notifications.length > 5 && (
          <Button onClick={clearAll} variant="ghost" className="w-full text-pink-300/60 hover:text-red-400 hover:bg-red-500/10 text-xs" data-testid="clear-all-notifs">
            <Trash2 className="w-4 h-4 mr-1" /> Effacer les notifications lues
          </Button>
        )}
      </div>

      <BottomNav active="notifications" />
    </div>
  );
};

export default NotificationsPage;
